import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Worker } from '@react-pdf-viewer/core'
import dayjs from 'dayjs'
import dayjsPluginTimezone from 'dayjs/plugin/timezone'
import dayjsPluginUTC from 'dayjs/plugin/utc'
import type React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { type CustomFormats, IntlProvider } from 'react-intl'
import { ToastContainer } from 'react-toastify'
import { SWRConfig } from 'swr'
import enGBYupMessages from 'yup-intl-validator/locales/en.json'
import '@react-pdf-viewer/core/lib/styles/index.css'
import '@react-pdf-viewer/default-layout/lib/styles/index.css'

import { ScrollToTop } from '@/helpers/ScrollToTop'
import { formatLocales, type TMessagesLocales } from '@/hooks/useFormattedLocales'
import enGBMessages from '@/locales/en-GB.json'
import { ForeignCurrencyValues } from '@/modules/_misc/enums/ForeignCurrency'
import { useLocaleContext, useSettingsContext } from '@/providers/Global'
import { Router } from '@/routes/_router'
import type { TLocales } from '@/services/Locale'
import '@/styles/toastify.css'
import { ThemeProvider } from '@/_minimal6/theme/theme-provider'

dayjs.extend(dayjsPluginUTC)
dayjs.extend(dayjsPluginTimezone)

//	///////////////////////
//	MARK: FORMATS
const formats: CustomFormats = {
  number: {
    percent: {
      style: 'percent',
    },
  },
}

for (const unit of ['hour', 'minute', 'second', 'pound', 'liter']) {
  formats.number![unit] = {
    style: 'unit',
    unit,
    unitDisplay: 'narrow',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }
}

for (const currency of ForeignCurrencyValues) {
  formats.number![currency] = {
    style: 'currency',
    currency,
  }
}

//	///////////////////////
//	MARK: FUNCTIONS
const getValidatorLocale = (locale: TLocales): (() => Promise<{ default: TMessagesLocales }>) =>
  ({
    'en-GB': () => import('yup-intl-validator/locales/en.json'),
    'fr-FR': () => import('yup-intl-validator/locales/fr.json'),
    'nl-NL': () => import('yup-intl-validator/locales/nl.json'),
  })[locale]

const getLocale = (locale: TLocales): (() => Promise<{ default: TMessagesLocales }>) =>
  ({
    'en-GB': () => import('@/locales/en-GB.json'),
    'fr-FR': () => import('@/locales/fr-FR.json'),
    'nl-NL': () => import('@/locales/nl-NL.json'),
  })[locale]

type TDayjsLocaleModule = { default: ILocale }

const getDayjsLocale = (locale: TLocales): (() => Promise<TDayjsLocaleModule>) =>
  ({
    'en-GB': () => import('dayjs/locale/en-gb'),
    'fr-FR': () => import('dayjs/locale/fr'),
    'nl-NL': () => import('dayjs/locale/nl'),
  })[locale]

// Mapping pour `LocalizationProvider.adapterLocale` (MUI x-date-pickers).
// Doit correspondre au nom d'une locale dayjs déjà chargée via `getDayjsLocale`.
const DAYJS_LOCALE_MAP: Record<TLocales, string> = {
  'en-GB': 'en-gb',
  'fr-FR': 'fr',
  'nl-NL': 'nl',
}

//	///////////////////////
//	MARK: APP
const App: React.FC = () => {
  const Settings = useSettingsContext()
  const Locale = useLocaleContext()

  // ///////////////////////
  // LOAD LOCALE
  const [messages, setMessages] = useState<{
    [key: string]: string
  }>({ ...enGBYupMessages, ...enGBMessages })

  const loadLocale = useCallback(() => {
    Promise.all([
      getValidatorLocale(Locale.active)(),
      getLocale(Locale.active)(),
      getDayjsLocale(Locale.active)(),
    ]).then((data) => {
      // Always force Monday as the first day of week (Europe convention)
      const dayjsLocale: ILocale = { ...data[2].default, weekStart: 1 }
      dayjs.locale(dayjsLocale)
      setMessages(formatLocales({ ...data[0].default, ...data[1].default }))
    })
  }, [Locale.active])

  useEffect(() => {
    loadLocale()
  }, [loadLocale])
  // ///////////////////////

  // ///////////////////////
  // RELOAD LOCALE ON CHANGE
  useEffect(() => {
    if (import.meta.hot) {
      import.meta.hot.on('locales-update', () => {
        loadLocale()
      })
    }
  }, [loadLocale])
  // ///////////////////////

  return (
    <Worker workerUrl='https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js'>
      <SWRConfig
        value={{
          refreshInterval: 60 * 1000,
          dedupingInterval: 60 * 1000,
        }}
      >
        <IntlProvider
          locale={Locale.active}
          messages={messages}
          formats={formats}
          timeZone={Settings.timeZone}
        >
          <ThemeProvider>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={DAYJS_LOCALE_MAP[Locale.active]}
              dateFormats={{
                keyboardDate: 'DD/MM/YYYY',
                fullDate: 'DD/MM/YYYY',
                keyboardDateTime24h: 'HH:mm',
                fullTime24h: 'HH:mm',
              }}
            >
              <ToastContainer position='bottom-right' autoClose={5000} />
              <ScrollToTop />
              <Router />
            </LocalizationProvider>
          </ThemeProvider>
        </IntlProvider>
      </SWRConfig>
    </Worker>
  )
}

export default App
